import { type EnvironmentId, type ThreadId } from "@cmmd-forge/contracts";
import { useCallback } from "react";
import { type useNavigate } from "@tanstack/react-router";
import { buildDraftThreadRouteParams } from "../../threadRoutes";
import { type DraftId } from "../../draftId";

export function useChatThreadNavigation(input: {
  readonly navigate: ReturnType<typeof useNavigate>;
  readonly routeKind: "server" | "draft";
  readonly draftId: DraftId | null;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly isLocalDraftThread: boolean;
  readonly isServerThread: boolean;
}): {
  readonly navigateActiveThreadSearch: (
    searchUpdater: (previous: Record<string, unknown>) => Record<string, unknown>,
  ) => void;
  readonly navigateToServerThreadSearch: (params: {
    readonly environmentId: EnvironmentId;
    readonly threadId: ThreadId;
    readonly search: (previous: Record<string, unknown>) => Record<string, unknown>;
  }) => void;
} {
  const {
    draftId,
    environmentId,
    isLocalDraftThread,
    isServerThread,
    navigate,
    routeKind,
    threadId,
  } = input;
  const navigateActiveThreadSearch = useCallback(
    (searchUpdater: (previous: Record<string, unknown>) => Record<string, unknown>) => {
      if (routeKind === "draft" && draftId && isLocalDraftThread) {
        navigate({
          to: "/draft/$draftId",
          params: buildDraftThreadRouteParams(draftId),
          replace: true,
          search: searchUpdater,
        });
        return;
      }
      if (!isServerThread) {
        return;
      }
      navigate({
        to: "/$environmentId/$threadId",
        params: {
          environmentId,
          threadId,
        },
        replace: true,
        search: searchUpdater,
      });
    },
    [draftId, environmentId, isLocalDraftThread, isServerThread, navigate, routeKind, threadId],
  );
  const navigateToServerThreadSearch = useCallback(
    (params: {
      readonly environmentId: EnvironmentId;
      readonly threadId: ThreadId;
      readonly search: (previous: Record<string, unknown>) => Record<string, unknown>;
    }) => {
      navigate({
        to: "/$environmentId/$threadId",
        params: {
          environmentId: params.environmentId,
          threadId: params.threadId,
        },
        replace: true,
        search: params.search,
      });
    },
    [navigate],
  );

  return { navigateActiveThreadSearch, navigateToServerThreadSearch };
}
